import React, { useState, useEffect } from 'react';
import Layout from '../components/layout/Layout';
import { Scissors, Leaf, Home, CloudSnow, Trash2, Sun, Sprout } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useIsMobile } from '@/hooks/use-mobile';
import useScrollToTop from '@/hooks/use-scroll-to-top';

const services = [
  {
    id: 'tonte-de-pelouse',
    title: 'Tonte de pelouse',
    description: 'Spécialité principale de notre entreprise, la tonte de pelouse est réalisée avec des équipements de pointe et une équipe expérimentée, garantissant un gazon impeccable et une expérience sans tracas. Nous offrons un service régulier et fiable pour maintenir votre pelouse en parfait état tout au long de la saison.',
    icon: <Scissors size={48} />,
    image: '/lovable-uploads/98c54ed9-ad98-4737-9c44-2f7967c22cc7.png',
    additionalImages: [
      {
        src: '/lovable-uploads/cb0f1424-4dcd-4096-93b1-ab5efa70c04e.png',
        alt: 'Tonte de pelouse avec lignes parfaites'
      },
      {
        src: '/lovable-uploads/36844efb-a60f-4620-88db-3f57be4ded97.png',
        alt: 'Pelouse parfaitement entretenue près d\'une résidence'
      },
      {
        src: '/lovable-uploads/86be24b1-a3c0-446c-a703-341cc2add3db.png',
        alt: 'Entretien professionnel de haie'
      },
      {
        src: '/lovable-uploads/fd7cb045-f10d-4295-91de-857c8dde7f65.png',
        alt: 'Tonte avec souffleur professionnel'
      }
    ],
    benefits: [
      'Équipement professionnel pour des coupes précises',
      'Calendrier d\'entretien régulier et fiable',
      'Personnel expérimenté et attentif aux détails',
      'Solutions adaptées à tous types de terrains'
    ]
  },
  {
    id: 'ouverture-de-terrain',
    title: 'Ouverture de terrain',
    description: 'Notre service de nettoyage printanier prépare votre cour à profiter pleinement de l\'été. Nous nous occupons de tous les débris accumulés pendant l\'hiver, réveillons votre pelouse et préparons vos plates-bandes pour une nouvelle saison de croissance.',
    icon: <Sun size={48} />,
    image: '/lovable-uploads/b0b1beb8-b76c-40e5-b40a-ff68b25f5cb8.png',
    additionalImages: [
      {
        src: '/lovable-uploads/ad3936e3-872f-43e5-842b-2e17aaa65f5d.png',
        alt: 'Travaux d\'aménagement paysager avec des briques'
      },
      {
        src: '/lovable-uploads/78ab4870-648c-421d-9942-5e81f7a8e31c.png',
        alt: 'Équipe travaillant sur l\'aménagement d\'un terrain'
      }
    ],
    benefits: [
      'Élimination complète des débris hivernaux',
      'Aération du sol pour favoriser la croissance',
      'Préparation des plates-bandes',
      'Évaluation de l\'état général du terrain'
    ]
  },
  {
    id: 'menage-automne',
    title: 'Ménage d\'automne',
    description: 'Notre service de ménage d\'automne prépare minutieusement votre terrain pour l\'hiver. Nous nous occupons du ramassage des feuilles, du nettoyage des plates-bandes et de la protection des éléments sensibles au froid, assurant ainsi que votre espace extérieur soit prêt à affronter l\'hiver.',
    icon: <Leaf size={48} />,
    image: '/lovable-uploads/4d4550a3-8c7a-4d2f-a660-b9d322ef85ea.png',
    additionalImages: [
      {
        src: '/lovable-uploads/7c03396c-100d-4d97-aaeb-911bf929726e.png',
        alt: 'Équipement pour nettoyage d\'automne'
      },
      {
        src: '/lovable-uploads/5e5b0657-f995-4b46-a966-62be4b17c0c9.png',
        alt: 'Terrain en nettoyage d\'automne'
      }
    ],
    benefits: [
      'Ramassage complet des feuilles',
      'Protection des plates-bandes pour l\'hiver',
      'Nettoyage des gouttières',
      'Préparation du terrain contre les dommages hivernaux'
    ]
  },
  {
    id: 'nettoyage-stationnement',
    title: 'Nettoyage de stationnement',
    description: 'Notre service de nettoyage de stationnement élimine efficacement les roches, le gravier, les déchets et les feuilles accumulés durant l\'hiver. Nous assurons un stationnement propre et présentable pour la saison estivale, améliorant ainsi l\'apparence générale de votre propriété.',
    icon: <Trash2 size={48} />,
    image: '/lovable-uploads/50bd2caa-82d3-46f1-b7f0-d757c8367a17.png',
    additionalImages: [
      {
        src: '/lovable-uploads/204167a9-27e2-447e-a370-c0e963133f50.png',
        alt: 'Stationnement commercial nettoyé professionnellement'
      },
      {
        src: '/lovable-uploads/0629e7cb-d632-4c60-9893-891ca9cf6f75.png',
        alt: 'Zone industrielle avec stationnement propre'
      },
      {
        src: '/lovable-uploads/077c0214-479b-43a4-a2d2-6ddb11af4979.png',
        alt: 'Grand stationnement commercial après nettoyage'
      },
      {
        src: '/lovable-uploads/252dfb40-22a3-429a-a9e0-661f2b63b8b9.png',
        alt: 'Stationnement avec camion de livraison et zone de chargement'
      }
    ],
    benefits: [
      'Élimination complète des débris d\'hiver',
      'Nettoyage en profondeur des surfaces',
      'Amélioration de l\'apparence et de la sécurité',
      'Service adapté aux résidences et commerces'
    ]
  },
  {
    id: 'deneigement-toitures',
    title: 'Déneigement de toitures',
    description: 'Notre service professionnel de déneigement de toitures est adapté tant aux résidences qu\'aux entreprises. Nous adoptons une approche préventive pour éviter l\'accumulation excessive de neige, protégeant ainsi la structure de votre bâtiment et prévenant les dommages potentiels.',
    icon: <Home size={48} />,
    image: '/lovable-uploads/6abdf836-2cad-4e26-8a19-d7d1d09992c6.png',
    additionalImages: [
      {
        src: '/lovable-uploads/62bd1a0b-0d2f-4518-ad22-0068ee7a9db4.png',
        alt: 'Déneigement d\'une toiture résidentielle en profondeur'
      },
      {
        src: '/lovable-uploads/7ee3b526-3413-462f-9bcc-9fa2d731a747.png',
        alt: 'Maison avec accumulation de neige sur le toit'
      }
    ],
    benefits: [
      'Prévention des dommages structurels',
      'Réduction des risques de fuites et d\'infiltrations',
      'Personnel formé aux techniques sécuritaires',
      'Interventions rapides après les chutes de neige importantes'
    ]
  },
  {
    id: 'deneigement-manuel',
    title: 'Terrassement / paysagement',
    description: 'Transformez votre extérieur avec Entretiens McBain!\n\nQue ce soit pour du terrassement, de l\'aménagement paysager, la création de plates-bandes, la restauration de pavé ou tout autre projet extérieur, notre équipe met son savoir-faire à votre service pour créer un espace soigné, durable et à la hauteur de vos attentes.',
    icon: <Sprout size={48} />,
    image: '/__l5e/assets-v1/d7f290aa-4180-4151-a9b8-8cb8764079ad/terrassement-pave.jpg',
    additionalImages: [
      {
        src: '/__l5e/assets-v1/18922a86-6794-4065-8fd2-6f49330e333a/pose-tourbe.jpg',
        alt: 'Équipe Entretiens McBain qui pose de la tourbe'
      },
      {
        src: '/__l5e/assets-v1/a0ee5bd8-3b8a-4974-8ea1-57a45e67b96f/tonte-pelouse-remorque.jpg',
        alt: 'Pelouse tondue en rayures avec la remorque Entretiens McBain sur place'
      }
    ],
    benefits: [
      'Terrassement et nivellement du terrain',
      'Aménagement paysager sur mesure',
      'Création de plates-bandes et restauration de pavé',
      'Travail soigné, durable et à la hauteur de vos attentes'
    ]
  }
];

const Services = () => {
  const isMobile = useIsMobile();
  const [activeService, setActiveService] = useState<string | null>(null);
  const [isScrolling, setIsScrolling] = useState(false);
  const scrollTimeout = React.useRef<number | null>(null);
  
  // Explicitly use scroll to top hook to ensure proper scrolling behavior
  useScrollToTop();
  
  // Improved scroll handling for mobile view
  useEffect(() => {
    if (!isMobile) return;
    
    const handleScroll = () => {
      // Set scrolling flag
      setIsScrolling(true);
      
      // Clear any existing timeout
      if (scrollTimeout.current) {
        window.clearTimeout(scrollTimeout.current);
      }
      
      // Set a timeout to detect when scrolling has stopped
      scrollTimeout.current = window.setTimeout(() => {
        setIsScrolling(false);
      }, 150);
    };
    
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (scrollTimeout.current) {
        window.clearTimeout(scrollTimeout.current);
      }
    };
  }, [isMobile]);

  // Function to toggle active service
  const toggleService = (serviceId: string) => {
    // Don't close the service if we're just scrolling
    if (isScrolling) return;
    
    setActiveService(activeService === serviceId ? null : serviceId);
  };

  return (
    <Layout>
      <div className="relative py-16 md:pt-20 md:pb-24 bg-brand-green text-white">
        <div className="absolute inset-0 overflow-hidden">
          <div 
            className="absolute inset-0 bg-cover bg-center opacity-20"
            style={{ backgroundImage: "url('/lovable-uploads/98c54ed9-ad98-4737-9c44-2f7967c22cc7.png')" }}
          ></div>
        </div>
        <div className="container-wide relative z-10 text-center pt-8 md:pt-16 pb-4 md:pb-8">
          <h1 className="section-title text-3xl md:text-5xl mb-4 md:mb-6">Nos Services</h1>
          <p className="text-lg md:text-xl text-gray-200 max-w-3xl mx-auto px-4">
            {isMobile ? 'Solutions d\'entretien paysager expertes' : 'Des solutions complètes d\'entretien paysager livrées avec expertise et attention aux détails'}
          </p>
        </div>
      </div>

      {/* Services List - Mobile Optimized */}
      <section className="py-10 md:py-16 bg-brand-stone-light">
        <div className="container-wide px-4">
          {isMobile ? (
            <div className="space-y-4">
              {services.map((service) => (
                <div key={service.id} className="bg-white rounded-lg shadow-md overflow-hidden">
                  <div 
                    className="flex items-center justify-between p-4 cursor-pointer"
                    onClick={() => toggleService(service.id)}
                  >
                    <div className="flex items-center">
                      <div className="bg-brand-stone rounded-full p-3 mr-3 text-brand-green">
                        {service.icon}
                      </div>
                      <h3 className="font-display text-xl">{service.title}</h3>
                    </div>
                    <svg 
                      xmlns="http://www.w3.org/2000/svg" 
                      className={`h-6 w-6 transition-transform ${activeService === service.id ? 'rotate-180' : ''}`}
                      fill="none" 
                      viewBox="0 0 24 24" 
                      stroke="currentColor"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                  
                  {activeService === service.id && (
                    <div className="p-4 border-t border-gray-100 animate-fade-in">
                      {service.image && (
                        <img 
                          src={service.image} 
                          alt={service.title} 
                          className="w-full h-48 object-cover rounded-lg mb-4"
                        />
                      )}
                      <p className="text-gray-600 mb-4">{service.description}</p>
                      
                      {service.benefits && (
                        <div className="mb-4">
                          <h4 className="font-display text-lg mb-2">Avantages:</h4>
                          <ul className="space-y-2">
                            {service.benefits.map((benefit, i) => (
                              <li key={i} className="flex items-start">
                                <svg className="h-5 w-5 text-brand-sage flex-shrink-0 mr-2 mt-1" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                  <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" />
                                  <path d="M8 12L11 15L16 9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                                <span className="text-sm">{benefit}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                      
                      {service.additionalImages && service.additionalImages.length > 0 && (
                        <div className="grid grid-cols-2 gap-2 mb-4">
                          {service.additionalImages.slice(0, 2).map((img, i) => (
                            <div key={i} className="rounded-md overflow-hidden">
                              <img 
                                src={img.src} 
                                alt={img.alt} 
                                className="w-full h-24 object-cover"
                              />
                            </div>
                          ))}
                        </div>
                      )}
                      
                      <Button 
                        href="/quote" 
                        size="sm"
                        className="w-full bg-brand-green text-white hover:bg-brand-green-dark"
                      >
                        Demander un devis
                      </Button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {services.map((service) => (
                <div key={service.id} className="bg-white rounded-lg shadow-md overflow-hidden transition-transform hover:-translate-y-1 hover:shadow-lg">
                  <div className="h-48 overflow-hidden">
                    <img 
                      src={service.image} 
                      alt={service.title} 
                      className="w-full h-full object-cover transition-transform hover:scale-105 duration-700"
                    />
                  </div>
                  <div className="p-6">
                    <div className="flex items-center mb-4">
                      <div className="text-brand-green mr-4">
                        {service.icon}
                      </div>
                      <h3 className="text-2xl font-display text-gray-900">{service.title}</h3>
                    </div>
                    <p className="text-gray-600 mb-4 whitespace-pre-line">{service.description}</p>
                    <a 
                      href={`#${service.id}`} 
                      className="inline-flex items-center px-4 py-2 border-2 border-[#1e312b] text-[#1e312b] rounded-md hover:bg-[#1e312b] hover:text-white transition-colors font-medium"
                    >
                      En savoir plus
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 ml-1" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                      </svg>
                    </a>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Individual Services (Detail Sections) - Only for Desktop */}
      {!isMobile && services.map((service, index) => (
        <section 
          id={service.id} 
          key={service.id}
          className={index % 2 === 0 ? "py-20 bg-white" : "py-20 bg-brand-stone-light"}
        >
          <div className="container-wide">
            <div className="flex flex-col lg:flex-row items-center gap-12">
              <div className={`w-full lg:w-1/2 ${index % 2 === 1 ? 'lg:order-2' : ''}`}>
                <img 
                  src={service.image} 
                  alt={service.title} 
                  className="rounded-lg shadow-xl w-full h-80 object-cover"
                />
                
                {/* Additional images gallery for select services */}
                {service.additionalImages && service.additionalImages.length > 0 && (
                  <div className="mt-6 grid grid-cols-2 gap-4">
                    {service.additionalImages.map((img, i) => (
                      <div key={i} className="image-gallery-item overflow-hidden rounded-lg shadow-md">
                        <img 
                          src={img.src} 
                          alt={img.alt} 
                          className="w-full h-48 object-cover transition-transform hover:scale-105 duration-500 service-image"
                        />
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className="w-full lg:w-1/2">
                <div className="text-brand-green mb-4">
                  {service.icon}
                </div>
                <h2 className="section-subtitle mb-4">{service.title}</h2>
                <p className="text-gray-700 mb-8">{service.description}</p>
                
                <h4 className="font-display text-xl mb-4">Avantages:</h4>
                <ul className="space-y-2 mb-8">
                  {service.benefits.map((benefit, i) => (
                    <li key={i} className="flex items-start">
                      <svg className="h-6 w-6 text-brand-sage flex-shrink-0 mr-2" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" />
                        <path d="M8 12L11 15L16 9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                      <span>{benefit}</span>
                    </li>
                  ))}
                </ul>
                
                <Button 
                  href="/quote" 
                  size="lg" 
                  variant="outline" 
                  className="border-[#1e312b] text-[#1e312b] hover:bg-[#1e312b] hover:text-white"
                >
                  Demander un devis
                </Button>
              </div>
            </div>
          </div>
        </section>
      ))}

      {/* CTA Section */}
      <section className="py-12 md:py-20 bg-brand-green text-white text-center">
        <div className="container-tight px-4">
          <h2 className="section-title text-3xl md:text-5xl mb-4 md:mb-6">
            {isMobile ? 'Prêt?' : 'Prêt à transformer votre espace extérieur?'}
          </h2>
          <p className="text-lg md:text-xl mb-6 md:mb-8 text-gray-200 max-w-3xl mx-auto">
            {isMobile ? 'Contactez-nous pour une consultation gratuite.' : 'Contactez-nous pour une consultation gratuite et laissez nos experts concrétiser votre vision.'}
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <Button 
              href="/contact" 
              size={isMobile ? "default" : "lg"}
              variant="outline" 
              className="border-white text-white hover:bg-white hover:text-brand-green"
            >
              Contactez-nous
            </Button>
            <Button 
              href="/quote" 
              size={isMobile ? "default" : "lg"}
              className="bg-white text-brand-green hover:bg-brand-stone"
            >
              {isMobile ? 'Devis gratuit' : 'Demander un devis gratuit'}
            </Button>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default Services;
